import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as UsersIcon } from "../../assets/icons/users-01.svg";
import ModalHeader from "../Modal/ModalHeader";
import { AppDispatch } from "../../store";
import { completeOnboarding } from "../../store/onboardingSlice";

interface PersonaReadySummary {
  title: string;
  role: string;
  suggestedQuestions: string[];
}

interface PersonaReadyModalProps {
  summary: PersonaReadySummary;
  onClose: () => void;
}

const PersonaReadyModal: React.FC<PersonaReadyModalProps> = ({ summary, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const closeAndComplete = () => {
    onClose();
    dispatch(completeOnboarding());
  };

  const handleQuestionClick = (question: string) => {
    closeAndComplete();
    navigate("/home/ai-analyst", { state: { initialInput: question } });
  };

  const handleGoHome = () => {
    closeAndComplete();
    navigate("/home", { replace: true });
  };

  // The summary can come back empty/malformed if the AI summarize call partially
  // failed. Detect that and show a clear error state instead of a blank card.
  const hasContent = Boolean(
    summary?.title || summary?.role || summary?.suggestedQuestions?.length
  );

  if (!hasContent) {
    return (
      <div className="flex flex-col">
        <ModalHeader
          title="Profile Summary Unavailable"
          icon={<UsersIcon className="h-6 w-6" />}
          onClose={onClose}
        />

        <div className="flex flex-col p-5 space-y-2">
          <p className="text-sm text-gray-900 font-Inter">
            We couldn't generate your profile summary just now.
          </p>
          <p className="text-sm text-gray-500 font-Inter">
            Your profile was still saved — you can head to Home and start using the
            AI Analyst right away.
          </p>
        </div>

        <div className="flex flex-row space-x-3 p-5">
          <button onClick={handleGoHome} className="flex-grow eai-btn-grn my-1 text-sm font-Inter">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ModalHeader
        title="Your Profile is Ready"
        icon={<UsersIcon className="h-6 w-6" />}
        onClose={onClose}
      />

      <div className="flex flex-col p-5 space-y-4">
        {/* Title & role */}
        <div>
          <p className="text-lg font-semibold text-gray-900 font-Inter">{summary?.title}</p>
          <p className="text-sm text-gray-500 mt-1 font-Inter">{summary?.role}</p>
        </div>

        {/* Suggested questions */}
        {summary?.suggestedQuestions?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-500 mb-2 font-Inter">Try asking the AI Analyst:</p>
            <div className="space-y-2">
              {summary.suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleQuestionClick(question)}
                  className="w-full text-left bg-gray-200 hover:bg-gray-300 transition-colors rounded-md px-4 py-3 text-sm text-gray-600 font-Inter"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-row space-x-3 p-5">
        <button onClick={handleGoHome} className="flex-grow eai-btn-grn my-1 text-sm font-Inter">
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default PersonaReadyModal;
